import React, { useState, useEffect } from 'react';
import { Loader2, AlertTriangle, Check, Wand2, Sofa, Maximize2, X } from 'lucide-react';
import { ComparisonSlider } from './ComparisonSlider';
import { VariantState } from '../types';

interface Props {
  variants: VariantState[];
  onSelect: (id: number) => void;
  isSaving: boolean;
}

const STATUS_LABELS: Record<VariantState['status'], string> = {
  pending: 'In wachtrij…',
  uploading: 'Uploaden…',
  renovating: 'Renovatie…',
  staging: 'Inrichten…',
  done: 'Klaar',
  error: 'Mislukt',
};

const BUSY_PHRASES = [
  'Licht analyseren…', 'Perspectief berekenen…', 'Meubels selecteren…',
  'Schaduwen renderen…', 'Texturen mappen…', 'Compositie optimaliseren…',
  'Kleurbalans ijken…', 'Ruimte herkennen…', 'Stijl toepassen…',
];

const SkeletonCard: React.FC<{ variant: VariantState }> = ({ variant }) => {
  const phrase = BUSY_PHRASES[(variant.id * 3 + Math.floor(variant.progress / 15)) % BUSY_PHRASES.length];
  return (
    <div className="border border-black/8 bg-white relative overflow-hidden aspect-[4/3] rounded-2xl shadow-[0_1px_2px_rgba(0,0,0,0.04)]">
      <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/60 to-transparent animate-[shimmer_2s_infinite]" />
      <div className="absolute inset-0 bg-stone-50/80" />
      <div className="relative z-10 h-full flex flex-col items-center justify-center gap-4 p-6">
        {variant.status === 'renovating' && <Wand2 size={32} className="text-accent/60 animate-pulse" />}
        {variant.status === 'staging' && <Sofa size={32} className="text-accent/60 animate-pulse" />}
        {variant.status === 'pending' && <Loader2 size={32} className="text-ink/50 animate-spin" />}
        <div className="w-3/4 bg-black/8 h-1.5 overflow-hidden rounded-full">
          <div className="h-full bg-accent transition-all duration-700 rounded-full" style={{ width: `${variant.progress}%` }} />
        </div>
        <span className="text-sm font-semibold text-ink/70 text-center">
          {phrase}
        </span>
        <span className="text-xs text-ink/45 font-medium">
          Variant {variant.id + 1} · {variant.progress}%
        </span>
      </div>
    </div>
  );
}

const ErrorCard: React.FC<{ variant: VariantState }> = ({ variant }) => {
  return (
    <div className="border border-red-200 bg-red-50 p-6 flex flex-col items-center justify-center aspect-[4/3] rounded-2xl">
      <AlertTriangle size={32} className="text-red-500 mb-3" />
      <p className="text-sm text-red-700 text-center font-bold mb-1.5">Variant {variant.id + 1} mislukt</p>
      <p className="text-xs text-red-600/80 text-center leading-relaxed">
        {variant.error || 'Onbekende fout'}
      </p>
    </div>
  );
}

const ResultCard: React.FC<{
  variant: VariantState;
  onSelect: () => void;
  onEnlarge: () => void;
  isSaving: boolean;
}> = ({ variant, onSelect, onEnlarge, isSaving }) => {
  return (
    <div className="border border-black/8 bg-white flex flex-col group rounded-2xl overflow-hidden shadow-[0_2px_8px_-2px_rgba(0,0,0,0.06)] hover:shadow-[0_12px_32px_-12px_rgba(0,0,0,0.18)] transition-all">
      <button
        type="button"
        onClick={onEnlarge}
        className="relative aspect-[4/3] overflow-hidden cursor-zoom-in block"
        aria-label={`Vergroot variant ${variant.id + 1}`}
      >
        <img
          src={variant.staged!}
          alt={`Variant ${variant.id + 1}`}
          className="w-full h-full object-cover group-hover:scale-[1.02] transition-transform duration-500"
        />
        <div className="absolute top-3 left-3 bg-white/95 backdrop-blur-sm text-ink px-2.5 py-1 text-[11px] font-bold rounded-full shadow-[0_2px_6px_rgba(0,0,0,0.1)]">
          Variant {variant.id + 1}
        </div>
        <div className="absolute top-3 right-3 w-9 h-9 bg-black/70 text-white rounded-full grid place-items-center opacity-0 group-hover:opacity-100 transition-opacity backdrop-blur-sm">
          <Maximize2 size={16} />
        </div>
      </button>
      <div className="p-3 border-t border-black/6">
        <button
          onClick={onSelect}
          disabled={isSaving}
          className="staging-primary-action"
        >
          {isSaving ? (
            <Loader2 size={16} className="animate-spin" />
          ) : (
            <Check size={16} strokeWidth={2.5} />
          )}
          Kies deze variant
        </button>
      </div>
    </div>
  );
}

const Lightbox: React.FC<{ src: string; beforeSrc?: string | null; label: string; onClose: () => void; onSelect?: () => void; isSaving?: boolean }> = ({ src, beforeSrc, label, onClose, onSelect, isSaving }) => {
  useEffect(() => {
    const handleKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', handleKey);
    return () => document.removeEventListener('keydown', handleKey);
  }, [onClose]);

  return (
    <div
      onClick={onClose}
      className="fixed inset-0 z-[100] bg-black/85 backdrop-blur-sm flex items-center justify-center p-4 md:p-10 animate-[fadeIn_0.15s_ease-out]"
    >
      <button
        onClick={onClose}
        className="absolute top-4 right-4 w-10 h-10 rounded-full bg-white/10 hover:bg-white/20 text-white grid place-items-center transition-colors z-10"
        aria-label="Sluiten"
      >
        <X size={18} />
      </button>
      <div className="absolute top-4 left-4 bg-white/10 backdrop-blur-sm text-white px-3.5 py-1.5 text-sm font-semibold rounded-full z-10">
        {label}
      </div>
      {onSelect && (
        <div className="absolute bottom-6 left-1/2 -translate-x-1/2 z-10" onClick={(e) => e.stopPropagation()}>
          <button
            onClick={onSelect}
            disabled={isSaving}
            className="staging-primary-action"
          >
            {isSaving ? <Loader2 size={16} className="animate-spin" /> : <Check size={16} strokeWidth={2.5} />}
            Kies deze variant
          </button>
        </div>
      )}
      <div
        onClick={(e) => e.stopPropagation()}
        className="w-full max-w-[1400px] max-h-full aspect-[4/3] rounded-lg overflow-hidden shadow-[0_30px_80px_-20px_rgba(0,0,0,0.6)]"
      >
        {beforeSrc ? (
          <ComparisonSlider beforeImage={beforeSrc} afterImage={src} labelBefore="Voor" labelAfter="Na" />
        ) : (
          <img src={src} alt={label} className="w-full h-full object-contain" />
        )}
      </div>
    </div>
  );
};

export const VariantSelector: React.FC<Props> = ({ variants, onSelect, isSaving }) => {
  const allDone = variants.every((v) => v.status === 'done' || v.status === 'error');
  const successCount = variants.filter((v) => v.status === 'done').length;
  const [lightboxId, setLightboxId] = useState<number | null>(null);
  const lightboxVariant = lightboxId !== null ? variants.find(v => v.id === lightboxId) : null;

  const total = variants.length;
  const gridCols = total === 1 ? 'md:grid-cols-1' : total === 2 ? 'md:grid-cols-2' : 'md:grid-cols-3';
  const maxW = total === 1 ? 'max-w-[900px]' : total === 2 ? 'max-w-[1400px]' : 'max-w-[1800px]';

  return (
    <div className="w-full h-full flex flex-col items-center justify-start md:justify-center p-4 md:p-8 overflow-auto">
      <div className={`w-full ${maxW}`}>
        <div className="mb-6 flex items-end justify-between gap-4">
          <div>
            <p className="text-xs font-semibold text-accent uppercase tracking-[0.1em] mb-1.5">
              {allDone ? `${successCount} van ${total} klaar` : 'Bezig met restylen…'}
            </p>
            <h3 className="font-sans text-2xl md:text-3xl font-bold text-ink tracking-[-0.02em]">Kies je favoriete versie</h3>
            {allDone && successCount > 0 && (
              <p className="text-sm text-ink/55 mt-1.5">Klik op een afbeelding om deze te vergroten.</p>
            )}
          </div>
        </div>

        <div className={`grid grid-cols-1 ${gridCols} gap-5 md:gap-6`}>
          {variants.map((v) => {
            if (v.status === 'error') return <ErrorCard key={v.id} variant={v} />;
            if (v.status === 'done' && v.staged) {
              return (
                <ResultCard
                  key={v.id}
                  variant={v}
                  onSelect={() => onSelect(v.id)}
                  onEnlarge={() => setLightboxId(v.id)}
                  isSaving={isSaving}
                />
              );
            }
            return <SkeletonCard key={v.id} variant={v} />;
          })}
        </div>
      </div>

      {lightboxVariant && lightboxVariant.staged && (
        <Lightbox
          src={lightboxVariant.staged}
          beforeSrc={lightboxVariant.original}
          label={`Variant ${lightboxVariant.id + 1}`}
          onClose={() => setLightboxId(null)}
          onSelect={() => { onSelect(lightboxVariant.id); setLightboxId(null); }}
          isSaving={isSaving}
        />
      )}
    </div>
  );
};
